/**
 * Lot AS : cartes « Tâche » de « À faire » (tâches ajoutées à la main, `user_task`) avec une description et des
 * « étapes » à cocher. Limites communes au serveur et à l'écran, avancement et découpage des liens de la
 * description (logique pure, testée).
 */

/** Longueur maximale de la description d'une tâche. */
export const TASK_DESCRIPTION_MAX = 10_000;
/** Longueur maximale du libellé d'une étape. */
export const TASK_STEP_LABEL_MAX = 500;
/** Nombre maximal d'étapes sur une carte. */
export const TASK_STEPS_MAX = 100;
/** Images d'une carte « Tâche » (second temps du lot AS) : 5 au plus, 5 Mo chacune, comme les retours. */
export const TASK_ATTACHMENT_MAX_COUNT = 5;
export const TASK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

/** Avancement des étapes : « 2/5 », 40 %. `total` = 0 : pas d'étape (pas de pastille). */
export interface ChecklistProgress {
  done: number;
  total: number;
  /** Pourcentage arrondi (0 sans étape). */
  percent: number;
  /** Toutes les étapes cochées (et au moins une). La carte reste ouverte jusqu'à « Fait ». */
  complete: boolean;
}

export function checklistProgress(items: readonly { done: boolean }[] | null | undefined): ChecklistProgress {
  const total = items?.length ?? 0;
  const done = items?.filter((i) => i.done).length ?? 0;
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    complete: total > 0 && done === total,
  };
}

/** Morceau de description : texte brut, ou lien cliquable (http ou https seulement). */
export type DescriptionSegment =
  { kind: 'text'; text: string } | { kind: 'link'; text: string; href: string };

/** Ponctuation qui suit souvent un lien dans une phrase (« voir https://x.fr. ») et n'en fait pas partie. */
const TRAILING = /^[.,;:!?…»"'’)\]]$/;
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;

/**
 * Découpe une description en texte et liens, pour l'afficher SANS `v-html` : l'écran rend chaque morceau comme
 * du texte (échappé par Vue) ou comme un `<a>` dont l'adresse est construite ici. Seules les adresses
 * `http://`, `https://` et `www.` deviennent des liens (jamais `javascript:` ni `data:`) ; la ponctuation finale
 * reste dans le texte ; une parenthèse fermante n'est gardée que si le lien en ouvre une (Wikipédia).
 */
export function descriptionSegments(text: string | null | undefined): DescriptionSegment[] {
  if (!text) return [];
  const segments: DescriptionSegment[] = [];
  let last = 0;
  const pushText = (value: string) => {
    if (!value) return;
    const prev = segments[segments.length - 1];
    if (prev?.kind === 'text') prev.text += value;
    else segments.push({ kind: 'text', text: value });
  };
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    let raw = match[0];
    // Ponctuation finale retirée un caractère à la fois ; une parenthèse fermante reste si le lien en ouvre une.
    let trimmed = raw;
    while (TRAILING.test(trimmed.slice(-1))) {
      if (trimmed.endsWith(')') && count(trimmed, '(') >= count(trimmed, ')')) break;
      trimmed = trimmed.slice(0, -1);
    }
    const tail = raw.slice(trimmed.length);
    raw = trimmed;
    const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
    pushText(text.slice(last, start));
    if (isSafeHref(href) && !/^(?:https?:\/\/|www\.)$/i.test(raw)) {
      segments.push({ kind: 'link', text: raw, href });
    } else {
      pushText(raw);
    }
    pushText(tail);
    last = start + match[0].length;
  }
  pushText(text.slice(last));
  return segments;
}

function count(text: string, char: string): number {
  let n = 0;
  for (const c of text) if (c === char) n += 1;
  return n;
}

/** Adresse sûre pour un lien : http(s) et un hôte, rien d'autre. */
export function isSafeHref(href: string): boolean {
  try {
    const url = new URL(href);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.length > 0;
  } catch {
    return false;
  }
}

/** Début de la description, sur une ligne (aperçu de la carte repliée). */
export function descriptionPreview(text: string | null | undefined, max = 160): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

/**
 * Nouvel ordre d'une liste quand l'élément `id` est déplacé à l'indice `to` (borné). Rend la même liste si
 * l'élément est inconnu ou ne bouge pas.
 */
export function moveInList<T extends { id: string }>(items: readonly T[], id: string, to: number): T[] {
  const from = items.findIndex((i) => i.id === id);
  if (from < 0) return [...items];
  const target = Math.max(0, Math.min(items.length - 1, to));
  if (target === from) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved as T);
  return next;
}

/**
 * Indice final d'un élément glissé depuis `from` et lâché sur l'élément `over`, avant ou après lui (`after`),
 * dans la liste d'origine. Ex. : 4 éléments, le premier lâché après le troisième → indice 2.
 */
export function dropTargetIndex(from: number, over: number, after: boolean): number {
  const slot = after ? over + 1 : over;
  return from < slot ? slot - 1 : slot;
}
